import { useState, useEffect } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import NapQrModal from "../modals/NapQrModal";
import NapEditModal from "../modals/NapEditModal";
import NapDetailsResultModal from "../modals/NapDetailsResultModal";
import { useConfirm } from "../../context/ConfirmContext";

/**
 * Shared NAP Details panel used by both NAP Locator Map View and NI Map View.
 *
 * Props:
 *  - selectedPoint: { id, nap_code, latitude, longitude, ... } or null
 *  - onClose: () => void — called when close button is clicked
 *  - onRecordUpdated: () => void — called after edit/delete/QR changes
 *  - onViewInTable: (lcpCode) => void | null — optional callback for "View on Table"
 *  - compact: boolean — if true, renders without its own wrapper card (caller provides container)
 */
export default function NapDetailsPanel({ selectedPoint, onClose, onRecordUpdated, onViewInTable, compact = false }) {
  const { hasPerm } = useAuth();
  const { confirm } = useConfirm();

  const [record, setRecord] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [qrModal, setQrModal] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [fetchingNapDetails, setFetchingNapDetails] = useState(false);
  const [napDetailsModal, setNapDetailsModal] = useState(null);

  const canEdit = hasPerm("nap_locator.edit");
  const canDelete = hasPerm("nap_locator.delete");
  const canQrGenerate = hasPerm("nap_locator.qr_generate");
  const canQrDelete = hasPerm("nap_locator.qr_delete");

  // Fetch full record when selectedPoint changes
  useEffect(() => {
    if (!selectedPoint?.id) { setRecord(null); return; }
    setRecordLoading(true);
    api.get("/nap-locator", { params: { search: selectedPoint.nap_code, page: 1, page_size: 1 } })
      .then((res) => {
        const found = res.data.find((r) => r.id === selectedPoint.id);
        setRecord(found || null);
      })
      .catch(() => setRecord(null))
      .finally(() => setRecordLoading(false));
  }, [selectedPoint]);

  const refetchRecord = () => {
    if (!selectedPoint) return;
    api.get("/nap-locator", { params: { search: selectedPoint.nap_code, page: 1, page_size: 1 } })
      .then((res) => {
        const found = res.data.find((r) => r.id === selectedPoint.id);
        if (found) setRecord(found);
      })
      .catch(() => {});
  };

  const handleGenerateQr = async () => {
    if (!record) return;
    setGeneratingQr(true);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/qr/preview`);
      setQrModal({ record, mode: "preview", previewData: data });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate QR preview.");
    } finally {
      setGeneratingQr(false);
    }
  };

  const handleNapDetails = async () => {
    if (!record) return;
    setFetchingNapDetails(true);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/nap-details`);
      if (data.found) {
        setNapDetailsModal(data);
        refetchRecord();
        onRecordUpdated();
      } else {
        await confirm({
          title: "No NAP Details Found",
          message: data.detail || `No NAP details found for "${record.nap_code}" on the external server.`,
          confirmLabel: "OK",
          cancelLabel: "Close",
        });
        try {
          await api.post(`/nap-locator/${record.id}/no-stencil`);
          refetchRecord();
          onRecordUpdated();
        } catch (e) {}
      }
    } catch (err) {
      await confirm({
        title: "NAP Details Error",
        message: err.response?.data?.detail || "Failed to fetch NAP details from external server.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        variant: "danger",
      });
    } finally {
      setFetchingNapDetails(false);
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    const ok = await confirm({
      title: "Delete NAP Record",
      message: `Are you sure you want to delete "${record.nap_code}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/nap-locator/${record.id}`);
      onClose();
      onRecordUpdated();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete record.");
    }
  };

  const handleQrSaved = () => {
    setQrModal(null);
    onRecordUpdated();
    refetchRecord();
  };

  const handleEditSaved = () => {
    setEditModal(false);
    onRecordUpdated();
    refetchRecord();
  };

  const content = (
    <>
      {recordLoading ? (
        <p className="text-xs text-gray-400 py-3 text-center">Loading...</p>
      ) : record ? (
        <div className="space-y-2 text-xs text-gray-700">
          <div><span className="text-gray-400">NAP Code:</span> <span className="font-semibold text-gray-800">{record.nap_code}</span></div>
          <div><span className="text-gray-400">No of Ports:</span> {record.no_of_ports ?? "—"}</div>
          <div><span className="text-gray-400">Latitude:</span> {record.latitude ?? "—"}</div>
          <div><span className="text-gray-400">Longitude:</span> {record.longitude ?? "—"}</div>
          <div><span className="text-gray-400">Area:</span> {record.area || "—"}</div>
          <div><span className="text-gray-400">Used Port:</span> {record.used_port ?? "—"}</div>
          <div><span className="text-gray-400">Unused/Vacant Port:</span> {record.vacant ?? "—"}</div>
          <div><span className="text-gray-400">ONU Connect:</span> {record.onu_connect ?? "—"}</div>
          <div><span className="text-gray-400">With Record:</span> {record.stencil ?? "—"}</div>
          <div><span className="text-gray-400">Updated By:</span> {record.updated_by_name || "—"}</div>

          {/* QR Code */}
          <div className="border-t pt-2 mt-2">
            {record.qr_code_data ? (
              <div className="flex flex-wrap items-start gap-1.5">
                <img
                  src={`data:image/png;base64,${record.qr_code_data}`}
                  alt="QR"
                  className="w-24 h-24 border rounded cursor-pointer hover:opacity-80"
                  onClick={() => setQrModal({ record, mode: "view", previewData: null })}
                />
                <button
                  onClick={handleNapDetails}
                  disabled={fetchingNapDetails}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {fetchingNapDetails ? "Fetching..." : "NAP Details"}
                </button>
              </div>
            ) : canQrGenerate ? (
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={handleGenerateQr}
                  disabled={generatingQr}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {generatingQr ? "Generating..." : "Generate QR"}
                </button>
                <button
                  onClick={handleNapDetails}
                  disabled={fetchingNapDetails}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {fetchingNapDetails ? "Fetching..." : "NAP Details"}
                </button>
              </div>
            ) : (
              <span className="text-gray-400">No QR code</span>
            )}
          </div>

          {/* Actions */}
          <div className="border-t pt-2 mt-2 flex flex-wrap gap-1.5">
            {canEdit && (
              <button onClick={() => setEditModal(true)} className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600">Edit</button>
            )}
            {canDelete && (
              <button onClick={handleDelete} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Delete</button>
            )}
            {onViewInTable && (
              <button
                onClick={() => {
                  const parts = record.nap_code.trim().split(/\s+/);
                  const lcp = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : record.nap_code;
                  onViewInTable(lcp);
                }}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                View on Table
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400 py-3 text-center">Select a NAP point on the map to view details.</p>
      )}

      {/* QR Modal */}
      {qrModal && (
        <NapQrModal
          record={qrModal.record}
          mode={qrModal.mode}
          previewData={qrModal.previewData}
          onClose={() => setQrModal(null)}
          onSaved={handleQrSaved}
          canDeleteQr={canQrDelete}
          canEdit={canEdit}
        />
      )}

      {/* Edit Modal */}
      {editModal && record && (
        <NapEditModal
          record={record}
          onClose={() => setEditModal(false)}
          onSaved={handleEditSaved}
        />
      )}

      {/* NAP Details Result Modal */}
      {napDetailsModal && (
        <NapDetailsResultModal
          data={napDetailsModal}
          onClose={() => setNapDetailsModal(null)}
          onChanged={() => { refetchRecord(); onRecordUpdated(); }}
        />
      )}

      {/* Loading overlay when fetching NAP details */}
      {fetchingNapDetails && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          <div className="relative flex items-center justify-center w-20 h-20">
            <svg
              className="absolute inset-0 w-full h-full animate-spin"
              style={{ animationDuration: "1.5s" }}
              viewBox="0 0 100 100"
              fill="none"
            >
              <polygon
                points="50,2 93,25 93,75 50,98 7,75 7,25"
                stroke="#2563eb"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="12 6"
              />
            </svg>
            <img
              src="/best-icon.png"
              alt="Loading..."
              className="h-12 w-12"
            />
          </div>
        </div>
      )}
    </>
  );

  if (compact) return content;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 flex-1 min-h-0 overflow-y-auto">
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">NAP Details</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm leading-none">&times;</button>
        </div>
        {content}
      </div>
    </div>
  );
}
